import { AdminAccessNotice } from "@/components/admin/access-notice";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState, useRef } from "react";
import { readAllRows } from "@/lib/read-all-rows";
import { operation } from "@/lib/api-client";
import { getCsrfToken } from "@/aws/client";
import { aws as awsClient } from "@/aws/client";
import { useAuth } from "@/hooks/use-auth";
import { useIsAdmin } from "@/hooks/use-is-admin";
import { AppLayout } from "@/components/app-layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, Plus, Pencil, Trash2, ShieldOff, GitBranch, Files, Import, Download } from "lucide-react";
import { exportSkillZip, exportAllSkillsZip } from "@/lib/skill-export";
import { toast } from "sonner";
import { SkillFileManager } from "@/components/admin/skill-file-manager";
import { GithubImportDialog } from "@/components/admin/github-import-dialog";
import { SkillBatchImportDialog } from "@/components/admin/skill-batch-import-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const Route = createFileRoute("/admin/skills")({
  component: AdminSkillsPage,
});

interface Skill {
  id: string;
  title: string;
  description: string | null;
  system_prompt: string;
  category: string | null;
  icon: string | null;
  updated_at: string;
}

function AdminSkillsPage() {
  const { user } = useAuth();
  const { isAdmin, loading: roleLoading, error: accessError, retry: retryAccess } = useIsAdmin();

  const [skills, setSkills] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Skill | null>(null);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [zipImporting, setZipImporting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Skill | null>(null);
  const [exportingId, setExportingId] = useState<string | null>(null);
  const [exportingAll, setExportingAll] = useState(false);

  const handleExportOne = async (s: Skill) => {
    setExportingId(s.id);
    try {
      await exportSkillZip(s);
      toast.success(`Exported "${s.title}".`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Export failed.");
    } finally {
      setExportingId(null);
    }
  };

  const handleExportAll = async () => {
    if (skills.length === 0) return;
    setExportingAll(true);
    try {
      await exportAllSkillsZip(skills, "askbell-skills.zip");
      toast.success(`Exported ${skills.length} skills.`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Export failed.");
    } finally {
      setExportingAll(false);
    }
  };

  const refresh = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const rows = await readAllRows<Skill>((from, to) => awsClient
        .from("skills")
        .select("id, title, description, system_prompt, category, icon, updated_at")
        .is("user_id", null)
        .order("title", { ascending: true })
        .order("id", { ascending: true })
        .range(from, to));
      if (rows.error) throw new Error(rows.error.message);
      setSkills(rows.data);
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : "Could not load skills.");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (isAdmin) refresh();
  }, [isAdmin]);

  if (roleLoading) {
    return (
      <AppLayout>
        <div className="flex h-full items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      </AppLayout>
    );
  }

  if (accessError && !roleLoading) return <AppLayout><div className="p-6"><AdminAccessNotice error={accessError} retry={retryAccess} /></div></AppLayout>;

  if (!isAdmin) {
    return (
      <AppLayout>
        <div className="flex h-full items-center justify-center px-6">
          <Card className="max-w-md">
            <CardContent className="p-8 text-center">
              <ShieldOff className="h-10 w-10 mx-auto text-muted-foreground" />
              <h1 className="mt-4 text-ui-xl font-bold text-foreground">Admins only</h1>
              <p className="mt-2 text-ui-base text-muted-foreground">
                You need the admin role to manage Skills.
              </p>
              <Button asChild variant="outline" className="mt-6">
                <Link to="/chat">Back to chat</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="flex-1 overflow-y-auto">
      <div className="mx-auto max-w-4xl px-6 py-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-ui-2xl font-bold text-foreground">Skills</h1>
            <p className="text-ui-base text-muted-foreground">
              Markdown system prompts users can toggle on per chat.
            </p>
          </div>
          <div className="flex gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">
                  <Import className="h-4 w-4" />
                  Import
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setImporting(true)}>
                  <GitBranch className="h-4 w-4 mr-2" />
                  From GitHub
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setZipImporting(true)}>
                  <Files className="h-4 w-4 mr-2" />
                  From files or ZIPs
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="outline"
              onClick={handleExportAll}
              disabled={loading || !!loadError || exportingAll || skills.length === 0}
            >
              {exportingAll ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              Export all
            </Button>
            <Button onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" />
              New skill
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : loadError ? (
          <Card><CardContent className="p-8 text-center space-y-3" role="alert">
            <p className="text-ui-base text-destructive">{loadError}</p>
            <Button variant="outline" onClick={refresh}>Retry loading skills</Button>
          </CardContent></Card>
        ) : skills.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-ui-base text-muted-foreground">
              No skills yet. Click "New skill" to add one.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {skills.map((s) => (
              <Card key={s.id}>
                <CardContent className="p-4 flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-ui-base font-bold text-foreground truncate">{s.title}</p>
                      {s.category && (
                        <span className="text-ui-xs uppercase tracking-wider text-muted-foreground">
                          {s.category}
                        </span>
                      )}
                    </div>
                    {s.description && (
                      <p className="text-ui-sm text-muted-foreground mt-0.5 line-clamp-2">
                        {s.description}
                      </p>
                    )}
                    <p className="text-ui-xs text-muted-foreground mt-1">
                      {s.system_prompt.length.toLocaleString()} chars
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      title="Export as ZIP"
                      disabled={exportingId === s.id}
                      onClick={() => handleExportOne(s)}
                    >
                      {exportingId === s.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4" />
                      )}
                    </Button>
                    <Button size="icon" variant="ghost" title="Edit" onClick={() => setEditing(s)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => setDeleteTarget(s)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
      </div>

      {(creating || editing) && (
        <SkillDialog
          skill={editing}
          userId={user?.id ?? ""}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            refresh();
          }}
        />
      )}

      <GithubImportDialog
        open={importing}
        onClose={() => setImporting(false)}
        onImported={async (newSkillId) => {
          setImporting(false);
          await refresh();
          // Open the new skill in the edit dialog so admin can review
          const { data } = await awsClient
            .from("skills")
            .select("id, title, description, system_prompt, category, icon, updated_at")
            .eq("id", newSkillId)
            .maybeSingle();
          if (data) setEditing(data as Skill);
        }}
      />

      <SkillBatchImportDialog
        open={zipImporting}
        onClose={() => setZipImporting(false)}
        onImported={async () => {
          setZipImporting(false);
          await refresh();
        }}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteTarget?.title}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the skill. Conversations that had it enabled will no longer
              receive its instructions.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (!deleteTarget) return;
                try {
                  await operation("system_skills_delete",{id:deleteTarget.id},getCsrfToken());
                  toast.success("Skill deleted.");
                  setDeleteTarget(null);
                  refresh();
                } catch { toast.error("Could not delete skill."); }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}

interface DialogProps {
  skill: Skill | null;
  userId: string;
  onClose: () => void;
  onSaved: () => void;
}

function SkillDialog({ skill, onClose, onSaved }: DialogProps) {
  const [title, setTitle] = useState(skill?.title ?? "");
  const [description, setDescription] = useState(skill?.description ?? "");
  const [category, setCategory] = useState(skill?.category ?? "");
  const [icon, setIcon] = useState(skill?.icon ?? "");
  const [body, setBody] = useState(skill?.system_prompt ?? "");
  const [saving, setSaving] = useState(false);
  const saveLock = useRef(false);
  const creationId = useRef(crypto.randomUUID());

  const save = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error("Title and body are required.");
      return;
    }
    if (saveLock.current) return;
    saveLock.current = true;
    setSaving(true);
    const payload = {
      title: title.trim(),
      description: description.trim() || null,
      category: category.trim() || null,
      icon: icon.trim() || null,
      system_prompt: body,
    };
    try {
      await operation("system_skills_save", {id:skill?.id ?? creationId.current, create:!skill, content:payload}, getCsrfToken());
      toast.success(skill ? "Skill updated." : "Skill created.");
      onSaved();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not save skill.");
    } finally {
      saveLock.current = false;
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{skill ? "Edit skill" : "New skill"}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="skill-title">Title *</Label>
              <Input
                id="skill-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Humanizer"
                maxLength={120}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="skill-category">Category</Label>
              <Input
                id="skill-category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="writing"
                maxLength={60}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="skill-desc">Description</Label>
            <Input
              id="skill-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Removes AI writing tells from output"
              maxLength={240}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="skill-icon">Icon (lucide name)</Label>
            <Input
              id="skill-icon"
              value={icon}
              onChange={(e) => setIcon(e.target.value)}
              placeholder="wand-2"
              maxLength={40}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="skill-body">Markdown body *</Label>
            <Textarea
              id="skill-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Paste the full markdown system prompt here…"
              className="min-h-[280px] font-mono text-ui-sm"
            />
            <p className="text-ui-xs text-muted-foreground">
              {body.length.toLocaleString()} characters
            </p>
          </div>

          {skill && (
            <div className="border-t border-border/60 pt-4">
              <SkillFileManager skillId={skill.id} />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {skill ? "Save changes" : "Create skill"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
